/**
 * Episode-aware date helpers for the Diagnosis section.
 *
 * An episode in Dx v2 is a contiguous run of status entries bounded by an
 * `ongoing === false` marker (the equivalent of CC's `vas === 0`). Entries
 * with `event === "delete"` or `markedIncorrect: true` are excluded from
 * segmentation, matching the backend transformer's `countable` filter.
 *
 * Both helpers take a raw statusHistory array + a fallback value so they
 * work uniformly against a diagnosis record (`d.statusHistory`) and a
 * trend-response payload (`data.audit`).
 */

const isCountable = (e) => !!e && e.event !== "delete" && !e.markedIncorrect;

/**
 * Start date of the MOST RECENT episode segment.
 *
 * For an ongoing diagnosis this is the current episode's start; for a
 * resolved diagnosis it is the start of the episode that just closed.
 * Algorithm: walk the countable audit backwards; skip a trailing resolve
 * (that closes the segment we want), find the previous resolve marker;
 * everything after it (or the whole chain if none) is the recent segment.
 * The FIRST entry of that segment carries the episode start `sinceWhen`.
 */
export const recentEpisodeSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  const countable = hist.filter(isCountable);
  if (countable.length === 0) return fallback;

  let i = countable.length - 1;
  if (countable[i].ongoing === false) i -= 1;
  let boundary = -1;
  for (; i >= 0; i -= 1) {
    if (countable[i].ongoing === false) {
      boundary = i;
      break;
    }
  }
  const start = countable[boundary + 1] || countable[0];
  return start.sinceWhen || fallback;
};

/**
 * Patient-reported date from the audit entry that CLOSED the most recent
 * episode — the last countable status row with `ongoing === false`. Used
 * for the "Resolved on" column / label instead of the backend's wall-clock
 * `resolvedAt`. Returns `fallback` if no countable resolve exists.
 */
export const recentResolveSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    const e = hist[i];
    if (!isCountable(e)) continue;
    if (e.ongoing === false) return e.sinceWhen || fallback;
  }
  return fallback;
};
